import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, ExternalLink } from "lucide-react";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  author: "1st Choice Editorial",
  cover_image: "",
  agent_name: "",
  agent_revenue: "",
};

export default function AdminBlogPanel() {
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editor, setEditor] = useState(null); // {mode:'create'|'edit', slug?, form}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch (_) {
      setPosts([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (slug) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/blog/${slug}`);
      toast.success("Post deleted.");
      setPosts((p) => p.filter((x) => x.slug !== slug));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...editor.form };
      if (editor.mode === "create") {
        const { data } = await api.post("/admin/blog", body);
        setPosts((p) => [data, ...p]);
        toast.success("Post created.");
      } else {
        const { data } = await api.put(`/admin/blog/${editor.slug}`, body);
        setPosts((p) => p.map((x) => (x.slug === editor.slug ? data : x)));
        toast.success("Post updated.");
      }
      setEditor(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const update = (k) => (e) =>
    setEditor((ed) => ({ ...ed, form: { ...ed.form, [k]: e.target.value } }));

  return (
    <div data-testid="admin-blog-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="numeric text-xs text-black/40 uppercase tracking-widest">
          {posts.length} posts
        </div>
        <button
          onClick={() => setEditor({ mode: "create", form: { ...EMPTY } })}
          data-testid="blog-new-button"
          className="btn-primary !py-2 !px-4 text-xs uppercase tracking-widest"
        >
          <Plus size={14} /> New post
        </button>
      </div>

      {busy ? (
        <div className="text-black/50 numeric text-sm">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="border border-dashed border-black/15 p-12 text-center text-black/50">
          No posts yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/40 font-mono">
                <th className="py-3 pr-4">Title</th>
                <th className="py-3 pr-4">Agent</th>
                <th className="py-3 pr-4">Revenue</th>
                <th className="py-3 pr-4">Created</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.slug} data-testid={`blog-row-${p.slug}`} className="border-b border-black/5">
                  <td className="py-4 pr-4">
                    <div className="text-[#0a0a0a] font-display text-lg font-light">{p.title}</div>
                    <div className="numeric text-[10px] text-black/40 mt-1">/blog/{p.slug}</div>
                  </td>
                  <td className="py-4 pr-4 text-sm text-black/70">{p.agent_name || "—"}</td>
                  <td className="py-4 pr-4 numeric text-xs text-[#121275]">{p.agent_revenue || "—"}</td>
                  <td className="py-4 pr-4 numeric text-xs text-black/50">
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-4 pr-4">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 text-black/60 hover:text-[#121275]"
                        title="View"
                      >
                        <ExternalLink size={14} />
                      </a>
                      <button
                        onClick={() => setEditor({ mode: "edit", slug: p.slug, form: { ...EMPTY, ...p } })}
                        data-testid={`blog-edit-${p.slug}`}
                        className="p-2 text-black/60 hover:text-[#0a0a0a]"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(p.slug)}
                        data-testid={`blog-delete-${p.slug}`}
                        className="p-2 text-black/60 hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEditor(null)}
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            data-testid="blog-editor"
            className="relative w-full max-w-2xl bg-[#f7f7f7] border border-black/15 p-8 max-h-[90vh] overflow-y-auto"
          >
            <button
              type="button"
              onClick={() => setEditor(null)}
              className="absolute top-4 right-4 text-black/60 hover:text-[#0a0a0a]"
            >
              <X size={20} />
            </button>
            <div className="overline">
              {editor.mode === "create" ? "New post" : "Edit post"}
            </div>
            <h3 className="font-display text-2xl text-[#0a0a0a] mt-2 font-light">
              {editor.mode === "create" ? "Add a producer story" : editor.form.title}
            </h3>

            <div className="mt-8 space-y-6">
              <div>
                <label className="field-label">Title</label>
                <input
                  required
                  className="input-line"
                  value={editor.form.title}
                  onChange={update("title")}
                  data-testid="blog-form-title"
                />
              </div>
              <div>
                <label className="field-label">Excerpt</label>
                <textarea
                  required
                  rows={2}
                  className="input-line resize-none"
                  value={editor.form.excerpt}
                  onChange={update("excerpt")}
                  data-testid="blog-form-excerpt"
                />
              </div>
              <div>
                <label className="field-label">Content (use blank lines for paragraphs)</label>
                <textarea
                  required
                  rows={8}
                  className="input-line resize-none"
                  value={editor.form.content}
                  onChange={update("content")}
                  data-testid="blog-form-content"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="field-label">Author</label>
                  <input className="input-line" value={editor.form.author} onChange={update("author")} />
                </div>
                <div>
                  <label className="field-label">Cover image URL</label>
                  <input
                    className="input-line"
                    value={editor.form.cover_image}
                    onChange={update("cover_image")}
                    data-testid="blog-form-cover"
                  />
                </div>
                <div>
                  <label className="field-label">Agent name</label>
                  <input className="input-line" value={editor.form.agent_name} onChange={update("agent_name")} />
                </div>
                <div>
                  <label className="field-label">Revenue stat (e.g. $42M / Y1)</label>
                  <input className="input-line" value={editor.form.agent_revenue} onChange={update("agent_revenue")} />
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button
                type="submit"
                disabled={saving}
                data-testid="blog-form-submit"
                className="btn-primary disabled:opacity-50"
              >
                {saving ? "Saving…" : editor.mode === "create" ? "Create post" : "Save changes"}
              </button>
              <button type="button" onClick={() => setEditor(null)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
